// Giải mã barcode/QR từ một ảnh tĩnh (ảnh chọn từ thư viện hoặc vừa chụp).
// Lazy-import html5-qrcode để thư viện (~80KB) không vào bundle chính — chỉ tải khi dùng.
//
// Ảnh chụp từ camera điện thoại thường rất lớn (~12MP). html5-qrcode giải mã ĐỒNG BỘ trên
// main thread → ảnh to làm treo UI vài giây và thường trượt (mã nhỏ so với ảnh, có thể bị
// xoay theo EXIF). Vì vậy ta THU NHỎ + vẽ lại qua canvas trước khi decode:
//   - canvas tự áp EXIF orientation (trình duyệt hiện đại) → ảnh thẳng, dễ nhận hơn.
//   - ảnh nhỏ → decode nhanh, hết đơ.

let seq = 0

// Cạnh dài tối đa sau khi thu nhỏ: đủ nét cho barcode/QR nhưng decode nhanh.
const MAX_DIM = 1600

const loadImage = (file) => new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file)
    const img = new Image()
    img.onload = () => resolve({ img, url })
    img.onerror = () => {
        URL.revokeObjectURL(url)
        reject(new Error('Không đọc được ảnh'))
    }
    img.src = url
})

// Thu nhỏ ảnh về <= MAX_DIM (giữ tỉ lệ). Trả về File mới; nếu lỗi/không cần thì trả file gốc.
const downscaleFile = async (file) => {
    let loaded
    try {
        loaded = await loadImage(file)
    } catch (e) {
        return file
    }
    const { img, url } = loaded
    // try/finally để object URL LUÔN được revoke kể cả khi getContext/drawImage throw
    // (hết bộ nhớ canvas trên máy yếu) — tránh rò rỉ blob ảnh ~12MP mỗi lần decode lỗi.
    let canvas
    try {
        const w = img.naturalWidth
        const h = img.naturalHeight
        const maxSide = Math.max(w, h)
        if (!maxSide || maxSide <= MAX_DIM) {
            return file
        }
        const scale = MAX_DIM / maxSide
        const cw = Math.round(w * scale)
        const ch = Math.round(h * scale)
        canvas = document.createElement('canvas')
        canvas.width = cw
        canvas.height = ch
        canvas.getContext('2d').drawImage(img, 0, 0, cw, ch)
    } catch (e) {
        return file
    } finally {
        URL.revokeObjectURL(url)
    }

    const blob = await new Promise((resolve) => {
        if (canvas.toBlob) canvas.toBlob(resolve, 'image/jpeg', 0.92)
        else resolve(null)
    })
    if (!blob) return file
    return new File([blob], 'scan.jpg', { type: 'image/jpeg' })
}

// html5-qrcode (scanFile) chỉ trả về DUY NHẤT 1 mã/ảnh. Giải mã 1 mã từ ảnh đã thu nhỏ.
const decodeSingle = async (prepared) => {
    const { Html5Qrcode } = await import('html5-qrcode')

    seq += 1
    const id = `request-photo-file-reader-${seq}`
    const el = document.createElement('div')
    el.id = id
    el.style.display = 'none'
    document.body.appendChild(el)

    const scanner = new Html5Qrcode(id, { verbose: false })
    try {
        const text = await scanner.scanFile(prepared, false)
        return (text || '').trim()
    } finally {
        try {
            scanner.clear()
        } catch (e) {
            // bỏ qua lỗi dọn dẹp
        }
        if (el.parentNode) el.parentNode.removeChild(el)
    }
}

// Các format mã đơn cần đọc (QR + barcode 1D) — dùng cho BarcodeDetector native.
const WANTED_FORMATS = ['qr_code', 'code_128', 'code_39', 'ean_13']

// BarcodeDetector native (Android Chrome, Chrome desktop...) đọc được NHIỀU mã trong 1 ảnh —
// cần cho yêu cầu "ảnh có nhiều QR". Trả mảng mã (có thể rỗng) nếu detector chạy được;
// trả null nếu KHÔNG hỗ trợ / lỗi → caller fallback về giải mã 1 mã (decodeSingle).
const detectMultiple = async (file) => {
    const BD = typeof window !== 'undefined' && window.BarcodeDetector
    if (!BD) return null
    try {
        // Chỉ khai báo format mà thiết bị thật sự hỗ trợ (khai báo format lạ sẽ throw).
        let formats = WANTED_FORMATS
        if (typeof BD.getSupportedFormats === 'function') {
            const supported = await BD.getSupportedFormats()
            formats = WANTED_FORMATS.filter(f => supported.indexOf(f) !== -1)
            if (!formats.length) return null // thiết bị không đọc được QR/barcode qua API này
        }
        const detector = new BD({ formats })
        const bitmap = await createImageBitmap(file)
        try {
            const results = await detector.detect(bitmap)
            return (results || []).map(r => (r.rawValue || '').trim()).filter(Boolean)
        } finally {
            if (bitmap && typeof bitmap.close === 'function') bitmap.close()
        }
    } catch (e) {
        return null // lỗi native → để caller fallback
    }
}

// Giải mã TẤT CẢ mã barcode/QR trong 1 ảnh. Trả mảng string (đã trim, đã bỏ rỗng).
// - Có BarcodeDetector: đọc được nhiều mã → validate "các QR có cùng 1 đơn không" (caller xử lý).
// - Không hỗ trợ: fallback về 1 mã (html5-qrcode) → mảng 0 hoặc 1 phần tử (degrade an toàn).
export const decodeImageCodes = async (file) => {
    const prepared = await downscaleFile(file)
    const many = await detectMultiple(prepared)
    if (many) return many
    const one = await decodeSingle(prepared)
    return one ? [one] : []
}

// Giải mã 1 mã từ ảnh (giữ API cũ cho nơi chỉ cần 1 mã).
export const decodeImageFile = async (file) => {
    const prepared = await downscaleFile(file)
    return decodeSingle(prepared)
}

export default decodeImageFile
